import { useState } from "react";
import { AlertTriangle, MapPin, Plus, Search, X } from "lucide-react";

import {
  type CarrierServiceAreaAssignment,
  type CarrierServiceAreaRow,
  useCarrierMe,
  useCarrierServiceAreas,
} from "@/api/carrier";
import {
  ColumnsMenu,
  TablePager,
  usePagedState,
} from "@/components/data-table";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DeclareServiceAreaDialog } from "@/features/declare-service-area-dialog";
import { EditServiceAreaDialog } from "@/features/edit-service-area-dialog";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { hasHttpStatus } from "@/lib/api-error";
import { cn } from "@/lib/utils";

const RESPONSIVE: Record<string, string> = {
  levels: "hidden lg:table-cell",
  utilization: "hidden xl:table-cell",
  vehicleCap: "hidden xl:table-cell",
  postcodes: "hidden lg:table-cell",
};

const TOGGLEABLE_COLUMNS = [
  { id: "levels", label: "Services" },
  { id: "utilization", label: "Utilization" },
  { id: "vehicleCap", label: "Vehicle cap" },
  { id: "postcodes", label: "Postcodes" },
];

interface CoverageRow {
  area: CarrierServiceAreaRow;
  assignment: CarrierServiceAreaAssignment;
}

export default function ServiceAreasScreen() {
  const me = useCarrierMe();
  const canManageOfferings =
    !me.error && me.data?.capabilities.canManageCarrierSettings === true;
  const { page, pageSize, setPage, setPageSize } = usePagedState();
  const serviceAreas = useCarrierServiceAreas({ all: true });
  const { isVisible, toggle: toggleColumn } = useColumnVisibility(
    "carrier-web:columns:service-areas"
  );
  const colClass = (id: string) =>
    isVisible(id) ? (RESPONSIVE[id] ?? "") : "hidden";
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<CoverageRow | null>(null);
  const [adding, setAdding] = useState(false);
  const serviceAreaRows: CarrierServiceAreaRow[] = Array.isArray(
    serviceAreas.data
  )
    ? serviceAreas.data
    : (serviceAreas.data?.data ?? []);
  const assignmentRows = serviceAreaRows.flatMap((area) =>
    area.carriers.map((assignment) => ({ area, assignment }))
  );
  const normalizedSearch = search.trim().toLowerCase();
  const filteredRows = assignmentRows.filter(({ area }) => {
    const matchesSearch =
      normalizedSearch.length === 0 ||
      [area.name, area.code, ...area.postcodes]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);
    return matchesSearch;
  });
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visibleRows = filteredRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const enabledServiceCount = assignmentRows.reduce(
    (sum, { assignment }) =>
      sum +
      assignment.serviceCoverages.filter((coverage) => coverage.active).length,
    0
  );
  const configuredAreaCount = assignmentRows.filter(({ assignment }) =>
    assignment.serviceCoverages.some((coverage) => coverage.active)
  ).length;
  const activeCount = assignmentRows.filter(
    ({ assignment }) => assignment.active
  ).length;
  const currentUtilization = assignmentRows.reduce(
    (sum, { assignment }) => sum + assignment.currentUtilization,
    0
  );
  const forbidden = hasHttpStatus(serviceAreas.error, 403);
  const blockingError =
    serviceAreas.error && (assignmentRows.length === 0 || forbidden);

  const resetFilters = () => {
    setSearch("");
    setPage(1);
  };

  return (
    <div className="carrier-page">
      <PageHeader
        title="Service areas"
        actions={
          !forbidden && canManageOfferings ? (
            <Button size="touch" onClick={() => setAdding(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add area
            </Button>
          ) : undefined
        }
      />

      {serviceAreas.isLoading && assignmentRows.length === 0 ? (
        <ServiceAreasLoading />
      ) : blockingError ? (
        <ServiceAreasError
          forbidden={forbidden}
          onRetry={() => void serviceAreas.refetch()}
        />
      ) : (
        <>
          {serviceAreas.error && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between"
            >
              <span>
                Coverage refresh failed. Loaded service-area data remains
                available.
              </span>
              <Button
                variant="outline"
                size="touch"
                className="bg-background"
                onClick={() => void serviceAreas.refetch()}
              >
                Try again
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <SummaryCard label="Assigned" value={assignmentRows.length} />
            <SummaryCard label="Active" value={activeCount} />
            <SummaryCard label="Enabled services" value={enabledServiceCount} />
            <SummaryCard label="Configured areas" value={configuredAreaCount} />
            <SummaryCard
              label="Orders in use"
              value={currentUtilization}
              className="col-span-2 lg:col-span-1"
            />
          </div>

          <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p>
              An active offering records where a service is available
              geographically. It does not guarantee a quote for every date: the
              exact lane needs a saved price or provider quote, and delivery
              depends on the selected induction plan and operating schedule.
            </p>
            {canManageOfferings && (
              <nav aria-label="Service setup" className="flex flex-wrap gap-3">
                <Button asChild variant="outline" size="sm-touch">
                  <a href="/services/shipping">Shipping services</a>
                </Button>
                <Button asChild variant="outline" size="sm-touch">
                  <a href="/pricing">Rate Workspace</a>
                </Button>
                <Button asChild variant="outline" size="sm-touch">
                  <a href="/settings/delivery">Delivery setup</a>
                </Button>
              </nav>
            )}
          </div>
          {me.error ? (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive sm:flex-row sm:items-center sm:justify-between"
            >
              <span>
                Couldn’t check service-area permissions. Configuration actions
                remain unavailable until permissions refresh.
              </span>
              <Button
                variant="outline"
                size="sm-touch"
                disabled={me.isFetching}
                onClick={() => void me.refetch()}
              >
                Retry permissions
              </Button>
            </div>
          ) : !me.isLoading && !canManageOfferings ? (
            <p
              role="status"
              className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
            >
              Service-area offerings, lane pricing, and delivery schedules are
              read-only here. Ask your Carrier Owner to change service-area
              offerings or other setup.
            </p>
          ) : null}
          <Card className="overflow-hidden">
            <CardHeader className="gap-3 space-y-0 border-b p-3 md:px-4 md:py-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <MapPin className="h-4 w-4" />
                    Coverage
                  </CardTitle>
                  <CardDescription className="mt-1 text-xs">
                    {assignmentRows.length} assigned area
                    {assignmentRows.length === 1 ? "" : "s"}
                  </CardDescription>
                </div>
                <div className="hidden md:block">
                  <ColumnsMenu
                    columns={TOGGLEABLE_COLUMNS}
                    isVisible={isVisible}
                    onToggle={toggleColumn}
                  />
                </div>
              </div>
              <div>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    aria-label="Search service areas"
                    placeholder="Search area, code, or postcode"
                    value={search}
                    className="pl-9 pr-10"
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                  />
                  {search && (
                    <Button
                      variant="ghost"
                      size="icon-sm-touch"
                      aria-label="Clear search"
                      className="absolute right-0 top-1/2 -translate-y-1/2"
                      onClick={() => {
                        setSearch("");
                        setPage(1);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>

            {assignmentRows.length === 0 ? (
              <EmptyCoverage
                canManage={canManageOfferings}
                onAdd={() => setAdding(true)}
              />
            ) : filteredRows.length === 0 ? (
              <NoCoverageResults onClear={resetFilters} />
            ) : (
              <>
                <div className="space-y-2 p-3 md:hidden">
                  {visibleRows.map((row) => (
                    <CoverageCard
                      key={row.assignment.id}
                      row={row}
                      canManage={canManageOfferings}
                      onEdit={() => setEditing(row)}
                    />
                  ))}
                </div>
                <CardContent className="hidden p-0 md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pl-4">Area</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className={colClass("levels")}>
                          Services
                        </TableHead>
                        <TableHead className={colClass("utilization")}>
                          Utilization
                        </TableHead>
                        <TableHead className={colClass("vehicleCap")}>
                          Vehicle cap
                        </TableHead>
                        <TableHead className={colClass("postcodes")}>
                          Postcodes
                        </TableHead>
                        <TableHead className="pr-4 text-right">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleRows.map(({ area, assignment }) => (
                        <TableRow key={assignment.id}>
                          <TableCell className="pl-4">
                            <div className="font-medium">{area.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {area.code}
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={assignment.active ? "ACTIVE" : "INACTIVE"}
                            />
                          </TableCell>
                          <TableCell
                            className={cn("text-sm", colClass("levels"))}
                          >
                            {formatServiceLevels(assignment)}
                          </TableCell>
                          <TableCell
                            className={cn("text-sm", colClass("utilization"))}
                          >
                            {formatUtilization(area, assignment)}
                          </TableCell>
                          <TableCell
                            className={cn("text-sm", colClass("vehicleCap"))}
                          >
                            {formatVehicleCap(area)}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "max-w-48 truncate text-sm text-muted-foreground",
                              colClass("postcodes")
                            )}
                            title={formatPostcodes(area.postcodes)}
                          >
                            {formatPostcodes(area.postcodes)}
                          </TableCell>
                          <TableCell className="pr-4 text-right">
                            {canManageOfferings && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditing({ area, assignment })}
                              >
                                Edit
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </>
            )}
          </Card>

          {filteredRows.length > 0 && (
            <TablePager
              page={currentPage}
              pageSize={pageSize}
              total={filteredRows.length}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={serviceAreas.isLoading}
            />
          )}
        </>
      )}

      {canManageOfferings && editing && (
        <EditServiceAreaDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          areaName={editing.area.name}
          platformMaxDailyOrders={editing.area.maxDailyOrders}
          assignment={editing.assignment}
        />
      )}

      {canManageOfferings && adding && (
        <DeclareServiceAreaDialog open onOpenChange={setAdding} />
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader className="flex-row items-center justify-between space-y-0 p-3">
        <CardDescription className="text-xs">{label}</CardDescription>
        <CardTitle className="text-xl tabular-nums">{value}</CardTitle>
      </CardHeader>
    </Card>
  );
}

function CoverageCard({
  row,
  canManage,
  onEdit,
}: {
  row: CoverageRow;
  canManage: boolean;
  onEdit: () => void;
}) {
  const { area, assignment } = row;
  return (
    <article className="rounded-lg border bg-background p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">{area.name}</h3>
          <p className="text-xs text-muted-foreground">{area.code}</p>
        </div>
        <StatusBadge status={assignment.active ? "ACTIVE" : "INACTIVE"} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        <CoverageDetail
          label="Services"
          value={formatServiceLevels(assignment)}
        />
        <CoverageDetail
          label="Utilization"
          value={formatUtilization(area, assignment)}
        />
        <CoverageDetail
          label="Postcodes"
          value={formatPostcodes(area.postcodes)}
        />
      </dl>
      {canManage && (
        <Button
          variant="outline"
          size="touch"
          className="mt-3 w-full"
          onClick={onEdit}
        >
          Edit coverage
        </Button>
      )}
    </article>
  );
}

function CoverageDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words font-medium">{value}</dd>
    </div>
  );
}

function ServiceAreasLoading() {
  const summarySkeletons = [
    "assigned",
    "active",
    "enabled-services",
    "configured-areas",
    "utilization",
  ];
  return (
    <div role="status" aria-label="Loading service areas" className="space-y-3">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
        {summarySkeletons.map((summary) => (
          <Skeleton key={summary} className="h-16" />
        ))}
      </div>
      <Skeleton className="h-72" />
    </div>
  );
}

function ServiceAreasError({
  forbidden,
  onRetry,
}: {
  forbidden: boolean;
  onRetry: () => void;
}) {
  return (
    <Card>
      <CardContent
        role="alert"
        className="flex flex-col items-center gap-3 p-8 text-center"
      >
        <AlertTriangle className="h-8 w-8 text-destructive" />
        <div>
          <h2 className="font-semibold">
            {forbidden
              ? "Service areas unavailable"
              : "Couldn’t load service areas"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {forbidden
              ? "You do not have permission to view carrier service areas."
              : "Check your connection and try again."}
          </p>
        </div>
        {!forbidden && (
          <Button variant="outline" size="touch" onClick={onRetry}>
            Try again
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyCoverage({
  canManage,
  onAdd,
}: {
  canManage: boolean;
  onAdd: () => void;
}) {
  return (
    <CardContent role="status" className="p-8 text-center">
      <MapPin className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
      <h2 className="font-semibold">No service areas assigned</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Add an EZX-defined area to make eligible services available in quotes.
      </p>
      {canManage && (
        <Button size="touch" className="mt-4" onClick={onAdd}>
          Add your first area
        </Button>
      )}
    </CardContent>
  );
}

function NoCoverageResults({ onClear }: { onClear: () => void }) {
  return (
    <CardContent role="status" className="p-8 text-center">
      <Search className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
      <h2 className="font-semibold">No matching service areas</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Try another area or postcode.
      </p>
      <Button variant="outline" size="touch" className="mt-4" onClick={onClear}>
        Clear filters
      </Button>
    </CardContent>
  );
}

function formatServiceLevels(row: CarrierServiceAreaAssignment): string {
  const names = row.serviceCoverages
    .filter((coverage) => coverage.active)
    .map((coverage) => coverage.carrierService.name);
  return names.length > 0 ? names.join(", ") : "None active";
}

function formatUtilization(
  area: CarrierServiceAreaRow,
  assignment: CarrierServiceAreaAssignment
): string {
  const cap = assignment.maxDailyOrders ?? area.maxDailyOrders;
  if (cap == null) return `${assignment.currentUtilization} used / no cap`;
  const percentage =
    cap > 0 ? Math.round((assignment.currentUtilization / cap) * 100) : 0;
  return `${assignment.currentUtilization} / ${cap} used (${percentage}%)`;
}

/**
 * The area's vehicle ceiling.
 *
 * No cap and a cap of zero are opposite states — one means unlimited, the
 * other means none may be assigned — so they must never render the same way.
 */
function formatVehicleCap(area: CarrierServiceAreaRow): string {
  if (area.maxVehicles == null) return "No cap";
  return area.maxVehicles === 1 ? "1 vehicle" : `${area.maxVehicles} vehicles`;
}

function formatPostcodes(postcodes: string[]): string {
  return postcodes.length > 0 ? postcodes.join(", ") : "None listed";
}
